<template>
  <v-dialog width="80%" v-model="showTest" persistent @keydown.esc="done">
    <template v-slot:activator="{ on, attrs }">
      <v-btn
        small
        class="ma-2"
        color="primary"
        style="min-width: 90px"
        v-bind="attrs"
        v-on="on"
      >
        {{ $t("tests.test.solveTest") }}
      </v-btn>
    </template>
    <v-card flat>
      <v-card-title>
        <span class="headline">{{ $t("tests.testEdit.title") }}</span>
        <v-spacer />
        <v-btn icon @click="$emit('close')"><v-icon>close</v-icon></v-btn>
      </v-card-title>
      <v-card-text class="px-12">
        <template v-if="notValidQuestions.length">
          <v-row justify="center">
            <v-icon large color="error">error </v-icon>
          </v-row>

          <div class="text-center">
            {{ $t("validation.notCorrectQuestions") }}
            {{ notValidQuestions.join() }}
          </div>
          <br />
        </template>
        <div class="text-center" v-text="$t('tests.test.info')"></div>
        <div
          v-if="allowedWrong"
          class="text-center"
          v-text="$tc('tests.test.allowedWrong', allowedWrong)"
        ></div>
        <v-stepper non-linear v-model="step" class="elevation-0">
          <v-stepper-header
            v-if="steps > 1"
            class="elevation-0"
            style="height: auto !important"
          >
            <template v-for="(q, i) in test.questions">
              <v-stepper-step
                :key="`step-${i}`"
                :step="i + 1"
                editable
                :rules="[() => !notValidQuestions.includes(i + 1)]"
              />
              <v-divider
                v-if="i + 1 !== steps"
                :key="`step-divider-${i}`"
              ></v-divider>
            </template>
          </v-stepper-header>

          <v-stepper-items>
            <v-stepper-content
              v-for="(q, j) in test.questions"
              :key="`content-${q.id}`"
              :step="j + 1"
            >
              <div v-text="q.question" class="title font-weight-bold" />
              <img
                v-if="q.file"
                :src="q.file.url"
                style="max-height: 100vh; max-width: 100%; height: auto; width: auto; display: block; margin: 0 auto;"
                class="my-4"
              />
              <template v-for="a in q.answers">
                <v-row :key="`answer-${a.id}`" dense align="center">
                  <v-checkbox
                    :key="`checkbox-${a.id}`"
                    class="mx-3"
                    v-model="aMap[a.id]"
                    :label="a.answer"
                    :style="isValidated ? 'pointer-events: none' : ''"
                    @change="checkQuestionAnswered(q.id)"
                  />
                </v-row>
              </template>
            </v-stepper-content>
          </v-stepper-items>
        </v-stepper>
      </v-card-text>
      <v-card-actions class="px-12 pb-4">
        <v-btn
          color="primary"
          text
          v-if="!isValidated"
          @click="$emit('read-more', { aMap, step })"
        >
          {{ $t("tests.test.read") }}
        </v-btn>
        <v-spacer />
        <template v-if="steps > 1">
          <v-btn
            color="primary"
            :text="step < 2"
            :disabled="step < 2"
            @click="step--"
          >
            {{ $t("tests.test.back") }}
          </v-btn>
          <v-btn
            color="primary"
            :text="!(step <= steps - 1)"
            :disabled="!(step <= steps - 1)"
            @click="step++"
          >
            {{ $t("tests.test.next") }}
          </v-btn>
        </template>
        <v-spacer />
        <v-btn
          color="primary"
          :disabled="!allQuestionsAnswered"
          :text="!allQuestionsAnswered"
          @click="validate()"
        >
          {{
            isValidated ? $t("tests.test.tryAgain") : $t("tests.test.validate")
          }}
        </v-btn>
        <v-btn
          color="primary"
          v-if="isValidated && ok"
          @click="$emit('test_done_w_mistakes')"
        >
          {{ $t("instructions.sign.create.action") }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import ValidateTestMutation from "@/graphql/mutation/instruction/test/ValidateTest.gql";

export default {
  props: ["test", "answerMap", "questionNr", "show"],
  data() {
    return {
      notValidQuestions: [],
      isValidated: false,
      ok: false,
      allowedWrong: 0,
      testStart: Date.now(),
      step: 1,
      showTest: false,
      questionsWithAnswers: new Set(),
      allQuestionsAnswered: false,
    };
  },
  mounted() {
    this.step = this.questionNr || 1;
  },
  computed: {
    aMap: {
      get() {
        return this.answerMap;
      },
      set(newVal) {
        return newVal;
      },
    },

    steps() {
      return this.test.questions.length;
    },
  },
  watch: {
    showTest() {
      this.$emit("is-test-visible", this.showTest);
    },
    show() {
      this.showTest = this.show;
    },
  },

  methods: {
    getDurationInSeconds() {
      return Math.round((Date.now() - this.testStart) / 1000);
    },
    validate() {
      if (this.isValidated) {
        this.$emit("try-again");
        return;
      }
      const answers = Object.entries(this.aMap).map(([id, isCorrect]) => ({
        id,
        isCorrect,
      }));

      this.$apollo
        .mutate({
          mutation: ValidateTestMutation,
          variables: {
            testId: this.test.id,
            durationSeconds: this.getDurationInSeconds(),
            answers,
          },
        })
        .then((resp) => {
          this.isValidated = true;
          this.ok = resp.data.validateTest.ok;
          this.allowedWrong = resp.data.validateTest.wrongQuestionsAllowedCount;
          const wrongQuestionIds = resp.data.validateTest.wrongQuestionIds;
          if (wrongQuestionIds.length == 0) {
            this.$emit("test-done");

            return;
          }

          this.test.questions.forEach((q, i) => {
            if (wrongQuestionIds.includes(q.id)) {
              this.notValidQuestions.push(i + 1);
            }
          });
        })
        .catch((err) =>
          this.$store.commit("push_global_message", {
            color: "error",
            text: err,
          })
        )
        .finally(() => {
          this.testStart = Date.now();
        });
    },
    checkQuestionAnswered(questionId) {
      const question = this.test.questions.find((q) => q.id === questionId);
      const hasAnswer = question.answers.some((answer) => this.aMap[answer.id]);

      if (hasAnswer) {
        this.questionsWithAnswers.add(questionId);
      } else {
        this.questionsWithAnswers.delete(questionId);
      }

      this.allQuestionsAnswered =
        [...this.questionsWithAnswers].length === this.test.questions.length;
    },
  },
};
</script>
